// Widget dashboard

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllWidgets, updateWidget, deleteWidget } from '../../services/widgetService';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const WidgetDashboard = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showTagOrdering, setShowTagOrdering] = useState(false);
  const [tagOrderingData, setTagOrderingData] = useState({});
  const [savingTagOrder, setSavingTagOrder] = useState({});
  const [sorting, setSorting] = useState("Last Updated");

  // Get widgets for home page categories with deduplication (from HomePage.js)
  const getHomePageWidgets = () => {
    const usedWidgets = new Set();
    const categories = [
      { name: 'indie', filter: w => w.indie && (w.title === "Add Listing" || (Array.isArray(w.tag) ? !w.tag.includes('people') : w.tag !== 'people')) },
      { name: 'new', filter: w => w.isNew },
      { name: 'people', filter: w => Array.isArray(w.tag) ? (w.tag.includes('people') || w.tag.includes('creators') || w.tag.includes('professionals')) : (w.tag === 'people' || w.tag === 'creators' || w.tag === 'professionals') },
      { name: 'openings', filter: w => Array.isArray(w.tag) ? w.tag.includes('openings') : w.tag === 'openings' },
      { name: 'analysis', filter: w => Array.isArray(w.tag) ? w.tag.includes('analysis') : w.tag === 'analysis' },
      { name: 'play', filter: w => Array.isArray(w.tag) ? w.tag.includes('play') : w.tag === 'play' },
      { name: 'learn', filter: w => Array.isArray(w.tag) ? w.tag.includes('learn') : w.tag === 'learn' },
      { name: 'books', filter: w => Array.isArray(w.tag) ? w.tag.includes('books') : w.tag === 'books' }
    ];

    const visibleWidgets = widgets.filter(w => !w.hidden && !w.isUpdate && w.title !== "Add Listing");
    const homeCategories = [];

    categories.forEach(category => {
      const categoryWidgets = visibleWidgets
        .filter(w => category.filter(w) && (category.name === 'new' ? !usedWidgets.has(w.title) : true));
      
      // Track all widgets for NEW category deduplication
      categoryWidgets.forEach(w => usedWidgets.add(w.title));
      
      if (categoryWidgets.length > 0) {
        homeCategories.push({
          name: category.name,
          widgets: categoryWidgets
        });
      }
    });

    return homeCategories;
  };

  useEffect(() => {
    fetchWidgets();
  }, []);

  const fetchWidgets = async (field = 'updatedAt', direction = 'desc') => {
    try {
      setLoading(true);
      setError(null);
      const widgetData = await getAllWidgets(field, direction);
      setWidgets(widgetData || []);
    } catch (err) {
      console.error('Error fetching widgets:', err);
      // If it's a permission error or collection doesn't exist, just show empty state
      if (err.message?.includes('Missing or insufficient permissions') || 
          err.message?.includes('collection') ||
          err.code === 'permission-denied') {
        setWidgets([]);
        setError(null);
      } else {
        setError('Failed to load widgets');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (widgetId) => {
    if (deleteConfirm !== widgetId) {
      setDeleteConfirm(widgetId);
      return;
    }

    try {
      setDeleting(true);
      await deleteWidget(widgetId);
      setWidgets(widgets.filter(widget => widget.id !== widgetId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting widget:', err);
      alert('Failed to delete widget. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Initialize tag ordering data using home page categories
  const initializeTagOrdering = () => {
    const orderingData = {};
    const homeCategories = getHomePageWidgets();
    
    homeCategories.forEach(category => {
      const categoryWidgets = category.widgets;
      
      // Sort widgets by their index for this category (0 goes to bottom)
      categoryWidgets.sort((a, b) => {
        const aIndex = a.index?.[category.name] || 0;
        const bIndex = b.index?.[category.name] || 0;
        
        // If both have index 0, maintain original order
        if (aIndex === 0 && bIndex === 0) return 0;
        // If only one has index 0, it goes to bottom
        if (aIndex === 0) return 1;
        if (bIndex === 0) return -1;
        // Otherwise sort by index
        return aIndex - bIndex;
      });
      
      orderingData[category.name] = categoryWidgets.map(widget => widget.id);
    });
    
    setTagOrderingData(orderingData);
  };

  // Move widget up in tag list
  const moveWidgetUp = (tag, widgetId) => {
    const currentOrder = [...tagOrderingData[tag]];
    const currentIndex = currentOrder.indexOf(widgetId);
    
    if (currentIndex > 0) {
      // Swap with previous item
      [currentOrder[currentIndex], currentOrder[currentIndex - 1]] = 
      [currentOrder[currentIndex - 1], currentOrder[currentIndex]];
      
      setTagOrderingData(prev => ({
        ...prev,
        [tag]: currentOrder
      }));
    }
  };

  // Move widget down in tag list
  const moveWidgetDown = (tag, widgetId) => {
    const currentOrder = [...tagOrderingData[tag]];
    const currentIndex = currentOrder.indexOf(widgetId);
    
    if (currentIndex < currentOrder.length - 1) {
      // Swap with next item
      [currentOrder[currentIndex], currentOrder[currentIndex + 1]] = 
      [currentOrder[currentIndex + 1], currentOrder[currentIndex]];
      
      setTagOrderingData(prev => ({
        ...prev,
        [tag]: currentOrder
      }));
    }
  };

  // Save tag ordering
  const saveTagOrdering = async (tag) => {
    try {
      setSavingTagOrder(prev => ({ ...prev, [tag]: true }));
      
      const widgetOrder = tagOrderingData[tag];
      const updatePromises = [];
      
      widgetOrder.forEach((widgetId, index) => {
        const widget = widgets.find(w => w.id === widgetId);
        if (widget) {
          const newIndex = index + 1; // Start from 1
          const updatedIndex = {
            ...widget.index,
            [tag]: newIndex
          };
          
          updatePromises.push(updateWidget(widgetId, { index: updatedIndex }));
        }
      });
      
      await Promise.all(updatePromises);
      
      // Refresh widgets to get updated data
      await fetchWidgets();
    } catch (err) {
      console.error('Error saving tag ordering:', err);
      alert('Failed to save ordering. Please try again.');
    } finally {
      setSavingTagOrder(prev => ({ ...prev, [tag]: false }));
    }
  };

  // Sort widget list based on criteria
  const sortList = async (field, direction) => {
    try {
      await fetchWidgets(field, direction);
    } catch (err) {
      console.error('Error saving tag ordering:', err);
      alert('Failed to save ordering. Please try again.');
    }
  };

  // Draggable widgets in category ordering section
  const handleDragEnd = (result, categoryName) => {
    if (!result.destination) return;
  
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;
  
    // Copy and reorder widgets
    const currentOrder = Array.from(tagOrderingData[categoryName] || []);
    const [moved] = currentOrder.splice(sourceIndex, 1);
    currentOrder.splice(destIndex, 0, moved);
  
    // Update state
    setTagOrderingData(prev => ({
      ...prev,
      [categoryName]: currentOrder,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading widgets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={fetchWidgets}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!embedded ? (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-100">Widgets</h2>
            <p className="text-gray-400 mt-2">Manage your TrendingChess widget content</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              ← Admin Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/widget/new')}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <span>+</span>
              New Widget
            </button>
          </div>
        </div>
      ) : null}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Total Widgets</h3>
          <p className="text-3xl font-bold text-blue-400">{widgets.length}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Visible</h3>
          <p className="text-3xl font-bold text-green-400">
            {widgets.filter(widget => !widget.hidden).length}
          </p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Hidden</h3>
          <p className="text-3xl font-bold text-yellow-400">
            {widgets.filter(widget => widget.hidden).length}
          </p>
        </div>
      </div>

      {/* Widget List */}
      {widgets.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="text-xl font-semibold text-gray-300 mb-4">No Widgets Yet</h3>
          <p className="text-gray-400 mb-6">Create your first widget to get started</p>
          <button
            onClick={() => navigate('/admin/widget/new')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Create First Widget
          </button>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-gray-100">Widgets</h3>
            <div className="flex items-center gap-1">
              {[
                { label: 'Updated', field: 'updatedAt', dir: 'desc', key: 'Last Updated' },
                { label: 'Created', field: 'createdAt', dir: 'desc', key: 'Last Created' },
                { label: 'A-Z', field: 'title', dir: 'asc', key: 'Alphabetical' },
                { label: 'Z-A', field: 'title', dir: 'desc', key: 'Reverse Alphabetical' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => { setSorting(s.key); sortList(s.field, s.dir); }}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    sorting === s.key
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Title</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Link</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Status</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Created</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Updated</th>
                  <th className="text-right py-4 px-6 text-gray-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {widgets.map((widget) => (
                  <tr key={widget.id} className="hover:bg-gray-750">
                    <td className="py-4 px-6">
                      <div>
                        <h4 className="text-gray-100 font-medium mb-1">
                          {widget.titled && widget.chessTitle && (
                            <span className="inline-flex items-center px-1.5 py-0.5 bg-red-600 text-white text-xs font-bold rounded mr-1">{widget.chessTitle}</span>
                          )}
                          {widget.title}
                        </h4>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {widget.link}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        !widget.hidden 
                          ? 'bg-green-900 text-green-200' 
                          : 'bg-yellow-900 text-yellow-200'
                      }`}>
                        {!widget.hidden ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-400 text-sm">
                      {formatDate(widget.createdAt)}
                    </td>
                    <td className="py-4 px-6 text-gray-400 text-sm">
                      {formatDate(widget.updatedAt)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        {widget.slug && (
                          <a
                            href={`/${widget.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 px-2 py-1 rounded transition-colors"
                            title={`View /${widget.slug}`}
                          >
                            👁
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/admin/widget/edit/${widget.id}`)}
                          className="text-green-400 hover:text-green-300 px-2 py-1 rounded transition-colors"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(widget.id)}
                          disabled={deleting}
                          className={`px-2 py-1 rounded transition-colors ${
                            deleteConfirm === widget.id
                              ? 'text-red-300 bg-red-900/50'
                              : 'text-red-400 hover:text-red-300'
                          }`}
                          title={deleteConfirm === widget.id ? 'Click again to confirm' : 'Delete'}
                        >
                          {deleteConfirm === widget.id ? '⚠️' : '🗑️'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetDashboard;